import Radio from "backbone.radio";
import { toast } from "extensions/toast";
import { startsWith } from "lodash-es";
import { captureException } from "extensions/sentry";

import { removeURLParameter } from "common/utils";
import { emitter } from "common/emitter";

import { DisabledAccountLogin, PasswordExpired, PazienError } from "common/helpers/errors";

// TODO: refactor with emitter
// eslint-disable-next-line import/no-cycle
import { request } from "common/components/request";
// eslint-disable-next-line import/no-cycle
import { pazienHistory } from "common/components/pazienHistory";

import {
    AUTHENTICATE, CLEAR_LAST_URL, INTERACT,
    STORE_LAST_URL, MIMIC, STOP_MIMIC, CLEAN_UP
} from "../reducers/authentication";

const validateLogin = function({ email = "", password = "" }) {
    const validation = {
        hasError: false,
        type: "",
        message: ""
    };
    
    if (!email.trim().length) {
        validation.type = "email";
        validation.message = "Enter your email address.";
        validation.hasError = true;
    } else if (!password.trim().length) {
        validation.type = "password";
        validation.message = "Enter your password";
        validation.hasError = true;
    }
    
    return validation;
};

export const RETURN_TO_CURRENT_URL_AFTER_LOGIN = function() {
    return (dispatch, getState) => {
        // Do not remove dates from the URL because we need to bring back the user to that URL after successful login
        dispatch(SET_LAST_URL(pazienHistory.getCurrentFragment(), { removeDatesFromURL: false }));
    };
};

export const SET_LAST_URL = function(lastUrl, { removeDatesFromURL = true } = {}) {
    return (dispatch, getState) => {
        // Don't remember default route or login/reset/saml kinda urls
        if (lastUrl === getState().config.defaultRouteMerchant || ["login", "logout", "reset", "new_user", "saml"].some((r) => startsWith(lastUrl, r))) {
            return;
        }
    
        if (typeof lastUrl !== "string") return;
    
        let url = lastUrl;
        if (removeDatesFromURL) {
            url = removeURLParameter({
                url: lastUrl,
                parameter: "dates"
            });
        }
    
        dispatch(STORE_LAST_URL(url));
    };
};

const HANDLE_AUTHENTICATION = function() {
    return (dispatch, getState) => {
        // Go to currently selected route or the default one
        const url = getState().authentication.url;
        if (url) {
            // Nullify entry in the store
            dispatch(CLEAR_LAST_URL());
            // Navigate to the url without any params
            pazienHistory.navigate(url, { trigger: true, replace: true });
        } else {
            return Promise.resolve(Radio.channel("main").request("get:user").fetchPrerequisites.then(() => {
                // Go to a default url with stored params like merchant.
                pazienHistory.navigateWithParams(
                    getState().user_merchant_settings.start_page || getState().config.defaultRouteMerchant,
                    { trigger: true, replace: true }
                );
            }));
        }
    };
};

let refresh_token_status = null;

export const REFRESH_TOKEN = function() {
    return async(dispatch, getState) => {
        const refresh_token = getState().authentication.refresh_token;
        if (!refresh_token) return false;
        
        // Prevent multiple calls to refresh the token
        try {
            if (refresh_token_status) return refresh_token_status;
            
            refresh_token_status = request("/api_token_refresh", {
                method: "POST",
                ensureAuth: false,
                data: {
                    token: refresh_token
                }
            });
        
            const { access_token: new_access_token, refresh_token: new_refresh_tokens } = await refresh_token_status;
            
            dispatch(AUTHENTICATE({
                access_token: new_access_token,
                refresh_token: new_refresh_tokens
            }));
            
            refresh_token_status = null;
    
            // If all good then return true to the action initiator
            return true;
        } catch (error) {
            // Catching everything here that can go wrong with refreshing the token:
            // - Refresh token has expired
            // - Request to refresh failed due to the network issue
            // - Unable to parse refresh token
            // - etc
            toast.error("There was an error refreshing your session");
            captureException(new PazienError({
                name: "Unable to refresh user session",
                originalError: error
            }));
    
            pazienHistory.navigateWithParams("logout", {
                trigger: true
            });
            return false;
        }
    };
};

export function DO_PASSWORD_UPDATE({ forgot = true, new_password, old_password, token }) {
    return async(dispatch, getState) => {
        let url = "/user/password/";
    
        if (forgot) {
            url = "/user/reset/";
        }
    
        return request(url, {
            ensureAuth: !token,
            refresh_token: false, // API returns 401 when old password is not valid
            method: "PATCH",
            data: { new_password, old_password },
            ...token && {
                headers: {
                    Authorization: token.includes(".") ? `JWT ${token}` : token
                }
            }
        }).then((data) => {
            if (data?.access_token && data?.refresh_token) {
                dispatch(AUTHENTICATE({
                    access_token: data.access_token,
                    refresh_token: data.refresh_token
                }));
                dispatch(INTERACT());
        
                return dispatch(HANDLE_AUTHENTICATION());
            }
            
            return data;
        }).catch((error) => {
            let errorMessage = "Unable to update your password. For assistance, please contact your team admin or Pazien directly at help@pazien.com";
            if (error.response.parsed?.title?.toLowerCase().indexOf("attempts") > -1) {
                errorMessage = "This account has been locked because of too many failed login attempts.";
            } else if (error.response.parsed?.title?.toLowerCase() === "wrong old password") {
                errorMessage = "Your old password didn't work. Let's try again";
            } else if (error.response.parsed?.description?.toLowerCase() === "already used") {
                errorMessage = "Your new password is not different from your last 10 passwords.";
            } else {
                captureException(new Error("Unknown password change error"), {
                    extra: {
                        request: {
                            duration: error.request.duration,
                            endTime: error.request.endTime,
                            startTime: error.request.startTime
                        },
                        status: error.response.status,
                        statusText: error.response.statusText
                    }
                });
            }
    
            throw new PazienError({
                message: errorMessage,
                originalError: error
            });
        });
    };
}

export function DO_LOGIN({ email, password }) {
    return async(dispatch, getState) => {
        // Do some pre-checks
        const validation = validateLogin({ email, password });
        if (validation.hasError) {
            const error = new Error(validation.message);
            error.type = validation.type;
            throw error;
        }
        
        return request("/user/login/", {
            ensureAuth: false,
            data: {
                get_jwt_auth: true
            },
            headers: {
                Authorization: `Basic ${window.btoa(`${email}:${password}`)}`
            }
        }).then(({ access_token, refresh_token }) => {
            dispatch(AUTHENTICATE({
                access_token,
                refresh_token
            }));
            dispatch(INTERACT());
    
            return dispatch(HANDLE_AUTHENTICATION());
        }).catch((error) => {
            const helpEmail = `<a href='mailto:${getState().config.helpEmail}'>${getState().config.helpEmail}</a>`;
    
            if (error.response?.status === 401 && error.response.parsed?.title === "Password expired") {
                // Store tokens and throw error to be picked and navigate a user to password change page
    
                const { reset_token } = JSON.parse(error.response.parsed.description);
                throw new PasswordExpired({
                    reset_token,
                    originalError: error
                });
            }
            
            if (!error.response || (error.response.status >= 500 && error.response.status < 600)) {
                throw new Error(`There was an internal error. Please try again or contact us at ${helpEmail}`);
            }
            if (error.response.parsed?.title?.toLowerCase().includes("user not found")) {
                throw new Error("The email and password you entered don't match.");
            }
            
            if (error.response.parsed?.title?.toLowerCase().indexOf("attempts") > -1) {
                throw new Error("This account has been locked because of too many failed login attempts.");
            }
            
            if (error.response.parsed?.title === "Your account has been locked") {
                // Remove timeout from the querystring
                pazienHistory.unsetQueryStringParameters({
                    param: "timeout",
                    trigger: false
                });
                throw new Error("Your account has been locked <i class='fas fa-question-circle pz-popover' data-toggle='popover' data-trigger='focus' title='Why was your account locked?' data-content='For your security, we lock accounts following too many unsuccessful sign-in attempts within a 30 minute timespan. Please try again in 30 minutes or contact your admin to immediately unlock your account.'></i>");
            }
            
            if (error.response.parsed?.title === "User account disabled") {
                if (error.response.parsed?.description === "Locked due to inactivity") {
                    throw new DisabledAccountLogin({
                        message: "Your account is currently locked. For your security, we lock accounts that have been inactive for more than 90 days. Please reset your password to unlock your account.",
                        originalError: error,
                        extra: {
                            description: "inactivity_lock"
                        }
                    });
                }
                throw new DisabledAccountLogin({
                    message: "Your account has been locked. Please contact your admin for assistance.",
                    originalError: error,
                    extra: {
                        description: "admin_lock"
                    }
                });
            }
            
            if (error.response.parsed?.title === "Invalid password.") {
                throw new Error("The email and password you entered don't match.");
            }
            
            if (error instanceof PazienError) throw error;
            
            throw new Error("The email and password you entered don't match.");
        });
    };
}

export const DO_SAML_AUTH = function({ refresh_token }) {
    return (dispatch, getState) => {
        // Save refresh token in the store
        dispatch(AUTHENTICATE({
            refresh_token
        }));
        dispatch(INTERACT());
        
        // Now exchange that refresh token for a new set of access & refresh tokens
        return dispatch(REFRESH_TOKEN());
    };
};

export const DO_MIMIC_USER = function(user_ref, path = "/") {
    return async(dispatch, getState) => {
        try {
            dispatch(MIMIC({
                mimic_as_user: user_ref,
                mimic_original_user: getState().common.user.username,
                mimic_original_path: window.location.href
            }));
            
            // Remove any overrides we have for a package
            localStorage.removeItem("package-override");
            
            // Reload the page (or go to a path) to have mimic take the effect
            window.location.href = path;
        } catch {
            toast.error("There was an error requesting mimic of a user");
        }
    };
};

export const STOP_MIMIC_USER = function() {
    return (dispatch, getState) => {
        const navigateAfter = getState().authentication.mimic_original_path || "/";
        dispatch(STOP_MIMIC());
        window.location.href = navigateAfter;
    };
};

export const RESET_APP_AUTH = function() {
    return (dispatch, getState) => {
        emitter.on("reset:user:success", () => {
            dispatch({
                type: "RESET_COMMON_STORE"
            });
            dispatch(CLEAN_UP());
            emitter.off("reset:user:success");
        });
        // Emit logout start event
        emitter.emit("logout:before");
        // Emit reset user event to clear active user instance
        // TODO: remove when user instance is fully moved to redux store and actions then we can keep only RESET_COMMON_STORE action
        emitter.emit("reset:user");
    };
};
